import type { jsPDF } from 'jspdf'
import {
	type InlineTextRun,
	parseMarkdownBoldRuns,
	stripMarkdownEmphasis,
} from '@/utils/markdownInline'

export type PdfTextRun = InlineTextRun

/**
 * Replace Unicode symbols with ASCII equivalents for jsPDF's default Helvetica font.
 * Unsupported glyphs render as garbled characters (e.g. "!'").
 */
export function sanitizeTextForPdf(text: string): string {
	return text
		.replace(/\u2192/g, '->') // →
		.replace(/\u2190/g, '<-') // ←
		.replace(/\u2193/g, 'v') // ↓
		.replace(/\u2191/g, '^') // ↑
		.replace(/\u2194/g, '<->') // ↔
		.replace(/\u21D2/g, '=>') // ⇒
		.replace(/\u21D0/g, '<=') // ⇐
		.replace(/\u27F6/g, '->') // ⟶
		.replace(/\u27F5/g, '<-') // ⟵
		.replace(/\u27F9/g, '=>') // ⟹
		.replace(/\u27F8/g, '<=') // ⟸
		.replace(/\u25BC/g, 'v') // ▼
		.replace(/\u25B2/g, '^') // ▲
		.replace(/\u25BE/g, 'v') // ▾
		.replace(/\u25B4/g, '^') // ▴
		.replace(/\u2014/g, '-') // em dash
		.replace(/\u2013/g, '-') // en dash
		.replace(/\u2026/g, '...') // ellipsis
		.replace(/\u2018|\u2019/g, "'") // smart single quotes
		.replace(/\u201C|\u201D/g, '"') // smart double quotes
		.replace(/\u00A0/g, ' ') // non-breaking space
}

function measureRun(doc: jsPDF, run: PdfTextRun, fontSize: number): number {
	doc.setFont('helvetica', run.bold ? 'bold' : 'normal')
	doc.setFontSize(fontSize)
	return doc.getTextWidth(run.text)
}

/**
 * Wrap sanitized text that may include `**bold**` into lines of styled runs.
 */
export function wrapRichTextForPdf(
	doc: jsPDF,
	text: string,
	maxWidth: number,
	fontSize: number
): PdfTextRun[][] {
	const sanitized = sanitizeTextForPdf(text)
	if (!sanitized) return [[{ text: '', bold: false }]]

	const lines: PdfTextRun[][] = []

	for (const paragraph of sanitized.split('\n')) {
		if (!paragraph) {
			lines.push([{ text: '', bold: false }])
			continue
		}

		const runs = parseMarkdownBoldRuns(paragraph)
		let currentLine: PdfTextRun[] = []
		let currentWidth = 0

		const flushLine = () => {
			if (currentLine.length === 0) {
				lines.push([{ text: '', bold: false }])
			} else {
				lines.push(currentLine)
			}
			currentLine = []
			currentWidth = 0
		}

		// Flush when the next token would overflow; if a single token is wider than the line, hard-break by character.
		const pushToken = (token: string, bold: boolean) => {
			if (!token) return
			const run = { text: token, bold }
			const width = measureRun(doc, run, fontSize)
			if (currentLine.length > 0 && currentWidth + width > maxWidth) {
				flushLine()
			}
			if (width <= maxWidth) {
				currentLine.push(run)
				currentWidth += width
				return
			}
			// Token alone exceeds maxWidth — hard-break by character (even on an empty line).
			let remaining = token
			while (remaining) {
				doc.setFont('helvetica', bold ? 'bold' : 'normal')
				doc.setFontSize(fontSize)
				let fit = remaining.length
				while (fit > 1 && doc.getTextWidth(remaining.slice(0, fit)) > maxWidth) {
					fit--
				}
				currentLine.push({ text: remaining.slice(0, fit), bold })
				flushLine()
				remaining = remaining.slice(fit)
			}
		}

		for (const run of runs) {
			const parts = run.text.split(/(\s+)/)
			for (const part of parts) {
				pushToken(part, run.bold)
			}
		}
		flushLine()
	}

	return lines.length > 0 ? lines : [[{ text: '', bold: false }]]
}

/** Draw one line of mixed normal/bold runs starting at (x, y). */
export function drawRichTextLine(
	doc: jsPDF,
	runs: PdfTextRun[],
	x: number,
	y: number,
	fontSize: number
): void {
	let cursorX = x
	for (const run of runs) {
		doc.setFont('helvetica', run.bold ? 'bold' : 'normal')
		doc.setFontSize(fontSize)
		doc.text(run.text, cursorX, y)
		cursorX += doc.getTextWidth(run.text)
	}
}

/**
 * Plain wrap: strip markdown then reuse rich wrap so long tokens hard-break the same way as body text.
 */
export function wrapTextForPdf(doc: jsPDF, text: string, maxWidth: number): string[] {
	const sanitized = stripMarkdownEmphasis(sanitizeTextForPdf(text))
	if (!sanitized) return ['']
	const fontSize = doc.getFontSize() || 10
	return wrapRichTextForPdf(doc, sanitized, maxWidth, fontSize).map((runs) =>
		runs.map((run) => run.text).join('')
	)
}
